import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import {
  Badge,
  Button,
  Card,
  CardContent,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/kit";
import type { InTransitReportQueryDTO } from "@/modules/inventory/domain/import-order-contracts";
import type {
  ImportOrderSource,
  ImportOrderStatus,
} from "@/server/platform/db/schema/inventory";
import { formatDateTimeLocal } from "@/shared/lib/date-time";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { Link, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import {
  useImportOrderSummary,
  useInTransitReportByOrder,
  useInTransitReportByVariant,
} from "../../api/import-orders-queries";
import {
  IMPORT_ORDER_SOURCE_LABELS,
  IMPORT_ORDER_STATUS_CLASS,
  IMPORT_ORDER_STATUS_LABELS,
  formatLakAmount,
} from "../../lib/import-order-labels";

const SOURCE_OPTIONS = [
  { value: "__all", label: "ທຸກແຫຼ່ງ" },
  ...(Object.keys(IMPORT_ORDER_SOURCE_LABELS) as ImportOrderSource[]).map(
    (source) => ({
      value: source,
      label: IMPORT_ORDER_SOURCE_LABELS[source],
    }),
  ),
];

const STATUS_OPTIONS = [
  { value: "__all", label: "ທຸກສະຖານະ (ເປີດ)" },
  { value: "ordered", label: IMPORT_ORDER_STATUS_LABELS.ordered },
  { value: "at_carrier", label: IMPORT_ORDER_STATUS_LABELS.at_carrier },
  {
    value: "partially_received",
    label: IMPORT_ORDER_STATUS_LABELS.partially_received,
  },
];

export function ImportInTransitReportPage() {
  const nav = useNavigate();
  const [sourceFilter, setSourceFilter] = useState<ImportOrderSource | "__all">(
    "__all",
  );
  const [statusFilter, setStatusFilter] = useState<ImportOrderStatus | "__all">(
    "__all",
  );
  const [overdueOnly, setOverdueOnly] = useState(false);

  const query = useMemo((): InTransitReportQueryDTO => {
    return {
      source: sourceFilter === "__all" ? undefined : sourceFilter,
      status: statusFilter === "__all" ? undefined : statusFilter,
      overdueOnly,
    };
  }, [sourceFilter, statusFilter, overdueOnly]);

  const summary = useImportOrderSummary();
  const byOrder = useInTransitReportByOrder(query);
  const byVariant = useInTransitReportByVariant(query);

  const orderRows = byOrder.data?.data ?? [];
  const variantRows = byVariant.data?.data ?? [];

  return (
    <>
      <Header />
      <Main className="flex flex-col gap-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex flex-col gap-1">
            <h2 className="font-display text-2xl text-foreground sm:text-3xl">
              ລາຍງານມູນຄ່າລະຫວ່າງທາງ
            </h2>
            <p className="text-muted-foreground text-sm">
              ສິນຄ້າທີ່ສັ່ງແລ້ວ ແຕ່ຍັງບໍ່ຮັບເຂົ້າສະຕັອກ
            </p>
            <div className="mt-2 h-0.5 w-16 rounded-full bg-secondary/70" />
          </div>
          <Button
            variant="outline"
            className="rounded-full"
            onClick={() => nav({ to: "/app/import-orders" })}
          >
            ກັບໄປໃບສັ່ງ
          </Button>
        </div>

        {summary.isLoading ? (
          <div className="grid gap-3 sm:grid-cols-3">
            {["a", "b", "c"].map((id) => (
              <Skeleton key={id} className="h-24 rounded-2xl" />
            ))}
          </div>
        ) : summary.data ? (
          <div className="grid gap-3 sm:grid-cols-3">
            <Card className="brand-card">
              <CardContent className="px-4 py-0">
                <p className="text-muted-foreground text-xs">ມູນຄ່າລວມ</p>
                <p className="font-display text-2xl tabular-nums">
                  {formatLakAmount(summary.data.totalInTransitValueLak)}
                </p>
              </CardContent>
            </Card>
            <Card className="brand-card">
              <CardContent className="px-4 py-0">
                <p className="text-muted-foreground text-xs">ເກີນກຳນົດ</p>
                <p className="font-display text-2xl text-destructive tabular-nums">
                  {formatLakAmount(summary.data.overdueInTransitValueLak)}
                </p>
              </CardContent>
            </Card>
            <Card className="brand-card">
              <CardContent className="px-4 py-0">
                <p className="text-muted-foreground text-xs">ແຫຼ່ງສັ່ງຫຼັກ</p>
                <div className="mt-1 flex flex-col gap-1 text-sm">
                  {summary.data.bySource.slice(0, 3).map((row) => (
                    <div
                      key={row.source}
                      className="flex justify-between gap-2"
                    >
                      <span>{IMPORT_ORDER_SOURCE_LABELS[row.source]}</span>
                      <span className="tabular-nums">
                        {formatLakAmount(row.valueLak)}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        ) : null}

        <div className="brand-card flex flex-wrap items-end gap-3 p-4">
          <div className="min-w-[10rem] flex-1">
            <p className="mb-1.5 text-muted-foreground text-xs">ແຫຼ່ງສັ່ງ</p>
            <SimpleSelect
              value={sourceFilter}
              onValueChange={(value) =>
                setSourceFilter(value as ImportOrderSource | "__all")
              }
              options={SOURCE_OPTIONS}
              className="h-10 w-full"
            />
          </div>
          <div className="min-w-[10rem] flex-1">
            <p className="mb-1.5 text-muted-foreground text-xs">ສະຖານະ</p>
            <SimpleSelect
              value={statusFilter}
              onValueChange={(value) =>
                setStatusFilter(value as ImportOrderStatus | "__all")
              }
              options={STATUS_OPTIONS}
              className="h-10 w-full"
            />
          </div>
          <Button
            type="button"
            size="sm"
            variant={overdueOnly ? "default" : "outline"}
            className="rounded-full"
            onClick={() => setOverdueOnly((prev) => !prev)}
          >
            ເກີນກຳນົດເທົ່ານັ້ນ
          </Button>
        </div>

        <Tabs defaultValue="order">
          <TabsList>
            <TabsTrigger value="order">ຕາມໃບສັ່ງ</TabsTrigger>
            <TabsTrigger value="variant">ຕາມສິນຄ້າ</TabsTrigger>
          </TabsList>

          <TabsContent value="order" className="mt-4">
            {byOrder.isLoading ? (
              <Skeleton className="h-40 rounded-xl" />
            ) : orderRows.length === 0 ? (
              <p className="text-muted-foreground text-sm">ບໍ່ມີຂໍ້ມູນ</p>
            ) : (
              <div className="flex flex-col gap-3">
                {orderRows.map((row) => (
                  <Link
                    key={row.id}
                    to="/app/import-orders/$id"
                    params={{ id: row.id }}
                    className="brand-card block p-4 transition-colors hover:bg-muted/30"
                  >
                    <div className="flex min-w-0 flex-wrap items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="break-all font-medium leading-snug">
                          {row.externalOrderNo}
                        </p>
                        <div className="mt-1 flex flex-wrap items-center gap-2">
                          <Badge
                            className={IMPORT_ORDER_STATUS_CLASS[row.status]}
                          >
                            {IMPORT_ORDER_STATUS_LABELS[row.status]}
                          </Badge>
                          {row.isOverdue ? (
                            <Badge variant="destructive">ເກີນກຳນົດ</Badge>
                          ) : null}
                        </div>
                        <p className="mt-1 break-all text-muted-foreground text-xs">
                          {IMPORT_ORDER_SOURCE_LABELS[row.source]} ·{" "}
                          {row.expectedArrivalDate
                            ? formatDateTimeLocal(row.expectedArrivalDate)
                            : "—"}
                        </p>
                      </div>
                      <div className="text-right text-sm">
                        <p className="font-medium tabular-nums">
                          {formatLakAmount(row.inTransitValueLak)}
                        </p>
                        <p className="text-muted-foreground text-xs">
                          ຄ້າງ {row.totalRemaining} ຊິ້ນ
                        </p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="variant" className="mt-4">
            {byVariant.isLoading ? (
              <Skeleton className="h-40 rounded-xl" />
            ) : variantRows.length === 0 ? (
              <p className="text-muted-foreground text-sm">ບໍ່ມີຂໍ້ມູນ</p>
            ) : (
              <div className="overflow-x-auto rounded-xl border">
                <table className="w-full min-w-[640px] text-sm">
                  <thead>
                    <tr className="border-b bg-muted/30 text-left text-muted-foreground">
                      <th className="px-4 py-3 font-medium">ສິນຄ້າ</th>
                      <th className="px-4 py-3 font-medium">ຄ້າງ</th>
                      <th className="px-4 py-3 font-medium">ໃບສັ່ງ</th>
                      <th className="px-4 py-3 text-right font-medium">ມູນຄ່າ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {variantRows.map((row) => (
                      <tr
                        key={row.variantId}
                        className="border-b last:border-0"
                      >
                        <td className="px-4 py-3">
                          <p className="font-medium">{row.productName}</p>
                          <p className="text-muted-foreground text-xs">
                            {row.variantName}
                          </p>
                        </td>
                        <td className="px-4 py-3 tabular-nums">
                          {row.remainingQuantity}
                        </td>
                        <td className="px-4 py-3 tabular-nums">
                          {row.orderCount}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums">
                          {formatLakAmount(row.inTransitValueLak)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </Main>
    </>
  );
}
